import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhPause";
import "@phosphor-icons/webcomponents/PhStop";
import "@phosphor-icons/webcomponents/PhBroadcast";
import "@phosphor-icons/webcomponents/PhExport";
import "@phosphor-icons/webcomponents/PhDotsThreeVertical";
import "@phosphor-icons/webcomponents/PhNotePencil";
import "@phosphor-icons/webcomponents/PhCopy";
import "@phosphor-icons/webcomponents/PhTrash";
import "ui-components/button/wow-button";
import "ui-components/dropdown-menu/dropdown-menu";
import "ui-components/dropdown-menu/dropdown-menu-item";
import "ui-components/status-badge/status-badge";
import "ui-components/image-or-video/image-or-video";

import { getAssetUrl } from "helpers/assetHelpers";
import type { LiveEvent } from "api-contracts/rpc/get-live-events";
import type { LiveEventStatus } from "ui-common/services/live-event-state/types";
import { t } from "../../translations/i18n";
import { styles } from "./upcoming-event-banner.css";

@customElement("upcoming-event-banner")
export class UpcomingEventBanner extends LitElement {
  static override styles = [styles];

  @property({ type: Object })
  event!: LiveEvent;

  private emit(name: string, detail?: unknown) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  private handleStreamMenuAction(e: CustomEvent) {
    this.emit("stream-action", { action: e.detail?.action, eventId: this.event.id });
  }

  private handleMenuAction(e: CustomEvent) {
    const action = e.detail?.action;
    switch (action) {
      case "share":
        this.handleShareClick();
        break;
      case "edit":
        this.emit("edit-event", { event: this.event });
        break;
      case "duplicate":
        this.emit("duplicate-event", { event: this.event });
        break;
      case "delete":
        this.emit("delete-event", { eventId: this.event.id });
        break;
    }
  }

  private handleShareClick() {
    this.emit("share-click", { event: this.event });
  }

  private handleStopLiveClick() {
    this.emit("stop-live", { eventId: this.event.id });
  }

  private handleEndEventClick() {
    this.emit("end-event", { eventId: this.event.id });
  }

  private renderStatusBadge() {
    const status = this.event.status as LiveEventStatus;
    const labelKey: Record<LiveEventStatus, Parameters<typeof t>[0]> = {
      scheduled: "liveEventsPage.scheduled",
      live: "liveEventsPage.live",
      ended: "liveEventsPage.ended",
      cancelled: "liveEventsPage.cancelled",
    };
    const variant = status === "live" ? "live" : "event";
    return html`
      <status-badge variant=${variant} size="medium" ?pulse=${status === "live"}> ${t(labelKey[status])} </status-badge>
    `;
  }

  override render() {
    const status = this.event.status;
    const startDate = new Date(this.event.scheduled_start_time);

    return html`
      <div class="upcoming-banner">
        ${this.event.thumbnail_url
          ? html`
              <image-or-video
                class="upcoming-banner-thumbnail"
                url=${getAssetUrl(this.event.thumbnail_url)}
                alt="${this.event.title}"
              ></image-or-video>
            `
          : nothing}
        <div class="upcoming-banner-content">
          <div class="upcoming-banner-header">
            <h3>${this.event.title}</h3>
          </div>
          <div class="upcoming-banner-bottom">
            <div class="upcoming-banner-info">
              ${this.renderStatusBadge()}
              <div class="upcoming-banner-date-meta">
                <span>${startDate.toLocaleDateString(undefined, { dateStyle: "long" }).toLowerCase()}</span>
                <span>${startDate.toLocaleTimeString(undefined, { timeStyle: "short" })}</span>
                ${this.event.fee_cents ? html`<span>&euro;${(this.event.fee_cents / 100).toFixed(2)}</span>` : nothing}
              </div>
            </div>
            <div class="upcoming-banner-stream-actions">
              ${status === "scheduled"
                ? html`
                    <dropdown-menu placement="bottom-end" @dropdown-item-click=${this.handleStreamMenuAction}>
                      <wow-button slot="trigger" variant="primary" size="medium" rounded>
                        ${t("liveEventsPage.startStreaming")}
                      </wow-button>
                      <dropdown-menu-item action="browser" icon-position="start">
                        <ph-broadcast slot="icon" size="16" weight="fill"></ph-broadcast>
                        ${t("liveEventsPage.startStreaming")}
                      </dropdown-menu-item>
                      <dropdown-menu-item action="obs" icon-position="start">
                        <ph-broadcast slot="icon" size="16"></ph-broadcast>
                        ${t("liveEventsPage.goLiveWithObs")}
                      </dropdown-menu-item>
                    </dropdown-menu>
                  `
                : nothing}
              ${status === "live"
                ? html`
                    <wow-button variant="neutral" size="small" @click=${this.handleStopLiveClick}>
                      <ph-pause size="16" weight="fill"></ph-pause>
                      ${t("liveEventsPage.stopLive")}
                    </wow-button>
                    <wow-button variant="danger" size="small" @click=${this.handleEndEventClick}>
                      <ph-stop size="16" weight="fill"></ph-stop>
                      ${t("liveEventsPage.endEvent")}
                    </wow-button>
                  `
                : nothing}
              <wow-button
                class="share-button-standalone"
                variant="subtle"
                size="small"
                square
                rounded
                .borderColor=${"var(--sds-color-border-default-secondary)"}
                @click=${this.handleShareClick}
                aria-label="Share event"
              >
                <ph-export size="20"></ph-export>
              </wow-button>
              <dropdown-menu placement="bottom-end" @dropdown-item-click=${this.handleMenuAction}>
                <wow-button
                  slot="trigger"
                  variant="subtle"
                  size="small"
                  square
                  rounded
                  .borderColor=${"var(--sds-color-border-default-secondary)"}
                  aria-label="More options"
                >
                  <ph-dots-three-vertical size="20"></ph-dots-three-vertical>
                </wow-button>
                <dropdown-menu-item action="edit" icon-position="start">
                  <ph-note-pencil slot="icon" size="16"></ph-note-pencil>
                  ${t("liveEventsPage.editEvent")}
                </dropdown-menu-item>
                <dropdown-menu-item class="share-menu-item" action="share" icon-position="start">
                  <ph-export slot="icon" size="16"></ph-export>
                  ${t("liveEventsPage.shareEvent")}
                </dropdown-menu-item>
                <dropdown-menu-item action="duplicate" icon-position="start">
                  <ph-copy slot="icon" size="16"></ph-copy>
                  ${t("liveEventsPage.duplicateEvent")}
                </dropdown-menu-item>
                ${status !== "live"
                  ? html`
                      <dropdown-menu-item action="delete" variant="danger" icon-position="start">
                        <ph-trash slot="icon" size="16"></ph-trash>
                        ${t("liveEventsPage.deleteEvent")}
                      </dropdown-menu-item>
                    `
                  : nothing}
              </dropdown-menu>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "upcoming-event-banner": UpcomingEventBanner;
  }
}
